import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, catchError, filter, map, of, switchMap, tap } from 'rxjs';
import { Auth, authState } from '../core/firebase/auth';
import {
  FALLBACK_LANGUAGE,
  LANGUAGES,
  TRANSLATIONS,
  isLanguageCode,
} from '../i18n/languages';
import { DATE_LOCALES } from '../i18n/locale';
import { LanguageCode } from '../types/rest-api';
import { FeatureFlagService } from './feature-flag.service';
import { UserApiService } from './user-api.service';

export const LANGUAGE_STORAGE_KEY = 'bn_language';
/**
 * Language choice shared with bildigsnetz.ch (website): same cookie on both,
 * see bildigsnetz-website roadmap «Remembered language».
 */
export const LANGUAGE_COOKIE = 'sprache';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const SHARED_DOMAIN = 'bildigsnetz.ch';

/** The cookie for all of bildigsnetz.ch; host-only on other hosts (localhost). */
export function languageCookie(
  code: LanguageCode,
  location: Pick<Location, 'hostname' | 'protocol'>
): string {
  const host = location.hostname.toLowerCase();
  const shared = host === SHARED_DOMAIN || host.endsWith(`.${SHARED_DOMAIN}`);
  return [
    `${LANGUAGE_COOKIE}=${code}`,
    ...(shared ? [`Domain=.${SHARED_DOMAIN}`] : []),
    'Path=/',
    `Max-Age=${COOKIE_MAX_AGE}`,
    'SameSite=Lax',
    ...(location.protocol === 'https:' ? ['Secure'] : []),
  ].join('; ');
}
/** Last known `multilanguage` switch, so the first paint is already right. */
export const MULTILANGUAGE_CACHE_KEY = 'bn_multilanguage';

/**
 * Language of the first paint: the preferred one when `multilanguage` was on
 * last time, else German. Also picks the locale for dates and numbers.
 */
export function initialLanguage(
  win: Window | null = globalThis.window ?? null
): LanguageCode {
  if (!win || readItem(MULTILANGUAGE_CACHE_KEY) !== '1') {
    return FALLBACK_LANGUAGE;
  }
  return preferredLanguage(win) ?? FALLBACK_LANGUAGE;
}

/**
 * `?lang=` from a link, then the choice shared with the website (cookie), then
 * the app's own stored choice, then the browser.
 */
function preferredLanguage(win: Window): LanguageCode | null {
  return (
    languageFromUrl(win) ??
    cookieLanguage(win) ??
    storedLanguage() ??
    languageFromBrowser(win)
  );
}

function readCookies(win: Window): string {
  try {
    return win.document?.cookie ?? '';
  } catch {
    return '';
  }
}

function cookieLanguage(win: Window): LanguageCode | null {
  const value = readCookies(win)
    .split(';')
    .map(part => part.trim())
    .find(part => part.startsWith(`${LANGUAGE_COOKIE}=`))
    ?.slice(LANGUAGE_COOKIE.length + 1)
    .toLowerCase();
  return isLanguageCode(value) ? value : null;
}

function languageFromUrl(win: Window): LanguageCode | null {
  const value = new URLSearchParams(win.location?.search ?? '')
    .get('lang')
    ?.toLowerCase();
  return isLanguageCode(value) ? value : null;
}

function languageFromBrowser(win: Window): LanguageCode | null {
  const navigator = win.navigator;
  for (const tag of navigator?.languages ?? [navigator?.language ?? '']) {
    const code = tag.slice(0, 2).toLowerCase();
    if (isLanguageCode(code)) {
      return code;
    }
  }
  return null;
}

function storedLanguage(): LanguageCode | null {
  const value = readItem(LANGUAGE_STORAGE_KEY);
  return isLanguageCode(value) ? value : null;
}

function readItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Language of the app. Behind the server switch `multilanguage`: while it is off
 * everyone sees German and the language cannot be changed.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translate = inject(TranslateService);
  private readonly features = inject(FeatureFlagService);
  private readonly userApi = inject(UserApiService);
  private readonly auth = inject(Auth);
  private readonly document = inject(DOCUMENT);
  private readonly dateAdapter = inject(DateAdapter, { optional: true });
  private readonly current = signal<LanguageCode>(FALLBACK_LANGUAGE);

  readonly language = this.current.asReadonly();
  readonly languages = LANGUAGES;
  readonly selectable = computed(() => this.features.multilanguage());

  /**
   * Registers the dictionaries and shows the preferred language right away
   * when the switch was on last time, else German.
   */
  init(): void {
    for (const code of LANGUAGES) {
      this.translate.setTranslation(code, TRANSLATIONS[code], true);
    }
    this.translate.setFallbackLang(FALLBACK_LANGUAGE);
    this.apply(initialLanguage(this.document.defaultView));
  }

  /** Loads the switch and, when it is on, the preferred or account language. */
  start(): Observable<void> {
    return this.features.load().pipe(
      tap(flags => {
        this.storeFlag(flags.multilanguage);
        if (!flags.multilanguage) {
          this.apply(FALLBACK_LANGUAGE);
          return;
        }
        const fromUrl = this.fromUrl();
        if (fromUrl) {
          this.store(fromUrl);
        }
        this.apply(this.preferred() ?? FALLBACK_LANGUAGE);
      }),
      filter(flags => flags.multilanguage),
      switchMap(() => authState(this.auth)),
      filter(user => !!user),
      switchMap(() =>
        this.userApi.getAccount().pipe(catchError(() => of(null)))
      ),
      tap(account => {
        if (account && isLanguageCode(account.language)) {
          this.apply(account.language);
          this.store(account.language);
        }
      }),
      map(() => undefined)
    );
  }

  /** Changes the language; when the account cannot be updated, the old one comes back. */
  setLanguage(code: LanguageCode): Observable<boolean> {
    if (!this.selectable() || !isLanguageCode(code)) {
      return of(false);
    }
    const previous = this.current();
    this.apply(code);
    this.store(code);
    if (!this.auth.currentUser) {
      return of(true);
    }
    return this.userApi.changeLanguage({ language: code }).pipe(
      map(() => true),
      catchError(() => {
        this.apply(previous);
        this.store(previous);
        return of(false);
      })
    );
  }

  private preferred(): LanguageCode | null {
    const win = this.document.defaultView;
    return win ? preferredLanguage(win) : null;
  }

  private fromUrl(): LanguageCode | null {
    const win = this.document.defaultView;
    return win ? languageFromUrl(win) : null;
  }

  private storeFlag(on: boolean): void {
    try {
      localStorage.setItem(MULTILANGUAGE_CACHE_KEY, on ? '1' : '0');
    } catch {
      // without storage the first paint is German until the switch is loaded
    }
  }

  private apply(code: LanguageCode): void {
    this.current.set(code);
    this.translate.use(code);
    this.document.documentElement.lang = code;
    this.dateAdapter?.setLocale(DATE_LOCALES[code]);
  }

  private store(code: LanguageCode): void {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
    } catch {
      // storage can be blocked; the account keeps the language anyway
    }
    try {
      this.document.cookie = languageCookie(code, this.document.location);
    } catch {
      // cookies can be blocked; the app still has its own stored choice
    }
  }
}
